import React from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";
import { CATEGORY_COLORS } from "../data/mockData";

const tooltipStyle = {
  backgroundColor: "#131520",
  border: "1px solid #1E2235",
  borderRadius: "12px",
  color: "#fff",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "13px",
};

export function BalanceTrendChart({ data }) {
  return (
    <div className="bg-bg-card border border-border rounded-2xl p-5 animate-slide-up" style={{ animationDelay: "200ms" }}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-display font-semibold text-white text-base">Balance Trend</h3>
          <p className="text-xs font-body text-white/30 mt-0.5">Monthly income vs expenses</p>
        </div>
        <div className="flex items-center gap-4 text-xs font-body">
          <span className="flex items-center gap-1.5 text-accent-green"><span className="w-2 h-2 rounded-full bg-accent-green" />Income</span>
          <span className="flex items-center gap-1.5 text-accent-red"><span className="w-2 h-2 rounded-full bg-accent-red" />Expenses</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
          <defs>
            <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00D4AA" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#00D4AA" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FF4D6D" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#FF4D6D" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 12, fontFamily: "DM Sans" }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 11, fontFamily: "DM Sans" }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`$${v.toLocaleString()}`, n.charAt(0).toUpperCase() + n.slice(1)]} />
          <Area type="monotone" dataKey="income" stroke="#00D4AA" strokeWidth={2} fill="url(#incomeGrad)" dot={{ fill: "#00D4AA", r: 4, strokeWidth: 0 }} />
          <Area type="monotone" dataKey="expenses" stroke="#FF4D6D" strokeWidth={2} fill="url(#expenseGrad)" dot={{ fill: "#FF4D6D", r: 4, strokeWidth: 0 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function SpendingBreakdownChart({ data }) {
  return (
    <div className="bg-bg-card border border-border rounded-2xl p-5 animate-slide-up" style={{ animationDelay: "300ms" }}>
      <div className="mb-5">
        <h3 className="font-display font-semibold text-white text-base">Spending Breakdown</h3>
        <p className="text-xs font-body text-white/30 mt-0.5">By category</p>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <ResponsiveContainer width={180} height={180}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={82} paddingAngle={3} dataKey="value">
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.color} stroke="transparent" />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v.toLocaleString()}`, "Spent"]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-2 w-full">
          {data.slice(0, 6).map(({ name, value, color }) => {
            const total = data.reduce((s, d) => s + d.value, 0);
            const pct = ((value / total) * 100).toFixed(1);
            return (
              <div key={name} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <span className="text-xs font-body text-white/50 flex-1 truncate">{name}</span>
                <span className="text-xs font-mono text-white/70">{pct}%</span>
                <div className="w-16 bg-white/5 rounded-full h-1">
                  <div className="h-1 rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function MonthlyBarChart({ data }) {
  return (
    <div className="bg-bg-card border border-border rounded-2xl p-5 animate-slide-up" style={{ animationDelay: "400ms" }}>
      <div className="mb-5">
        <h3 className="font-display font-semibold text-white text-base">Monthly Overview</h3>
        <p className="text-xs font-body text-white/30 mt-0.5">Net balance per month</p>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -10 }} barSize={24}>
          <defs>
            <linearGradient id="balanceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.6} />
            </linearGradient>
          </defs>
          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 12, fontFamily: "DM Sans" }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 11, fontFamily: "DM Sans" }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`$${v.toLocaleString()}`, "Balance"]} />
          <Bar dataKey="balance" fill="url(#balanceGrad)" radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
